import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/Spinner';
import { US_STATES } from '@/lib/reference';
import type { AdminUserFormValues } from '@/types/api';

export default function AdminUserFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const id = params.id ? Number(params.id) : null;
  const isEdit = id !== null;
  const [banner, setBanner] = useState<string | null>(null);

  // Schema depends on create vs edit (password required only when creating).
  const schema = z
    .object({
      username: z.string().min(1, 'Username is required'),
      email: z.string().min(1, 'Email is required').email('Enter a valid email'),
      password: z.string(),
      confirmPassword: z.string(),
      role: z.coerce.number(),
      organizationId: z.coerce.number().min(1, 'Select an organization'),
      name: z.string(),
      address: z.string(),
      city: z.string(),
      state: z.string(),
      zip: z.string(),
    })
    .superRefine((val, ctx) => {
      if (!isEdit && !val.password)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Password is required for a new user' });
      if (val.password && val.password !== val.confirmPassword)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirmPassword'], message: 'Passwords do not match' });
    });
  type Values = z.infer<typeof schema>;

  const EMPTY: Values = {
    username: '', email: '', password: '', confirmPassword: '',
    role: 0, organizationId: 0, name: '', address: '', city: '', state: 'OK', zip: '',
  };

  const { data: orgs = [] } = useQuery({ queryKey: ['admin-orgs'], queryFn: () => api.getOrganizations() });
  const { data: existing, isLoading } = useQuery({
    queryKey: ['admin-user', id], queryFn: () => api.getAdminUser(id as number), enabled: isEdit,
  });

  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  useEffect(() => {
    if (existing) reset({ ...EMPTY, ...existing, password: '', confirmPassword: '' });
  }, [existing, reset]);

  const onSubmit = async (values: Values) => {
    setBanner(null);
    const payload = values as AdminUserFormValues;
    const res = isEdit ? await api.updateAdminUser(id as number, payload) : await api.createAdminUser(payload);
    if (res.err === '1') {
      // Refresh the user list (and this user) so the change shows without a refresh.
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      if (isEdit) queryClient.invalidateQueries({ queryKey: ['admin-user', id] });
      navigate('/admin');
    } else {
      setBanner(typeof res.message === 'string' ? res.message : 'Could not save user.');
    }
  };

  if (isEdit && isLoading) {
    return <div className="ast-cpl text-center py-5"><Spinner /></div>;
  }

  return (
    <div className="ast-cpl ast-cpl-form">
      <div className="ast-page-head">
        <h2 className="serif ast-page-title">{isEdit ? 'Edit User' : 'New User'}</h2>
      </div>

      {banner && <div className="alert alert-danger py-2" role="alert">{banner}</div>}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Account Info */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Account Info</h3>
          <div className="ast-grid-2">
            <FormField label="Username" htmlFor="au-username" error={errors.username?.message}>
              <input id="au-username" className="form-control form-control-sm" {...register('username')} />
            </FormField>
            <FormField label="Email" htmlFor="au-email" error={errors.email?.message}>
              <input id="au-email" type="email" className="form-control form-control-sm" {...register('email')} />
            </FormField>
            <FormField label="New Password" htmlFor="au-password" error={errors.password?.message}>
              <input id="au-password" type="password" autoComplete="new-password" className="form-control form-control-sm" {...register('password')} />
            </FormField>
            <FormField label="Confirm New Password" htmlFor="au-confirm" error={errors.confirmPassword?.message}>
              <input id="au-confirm" type="password" autoComplete="new-password" className="form-control form-control-sm" {...register('confirmPassword')} />
            </FormField>
            <FormField label="Organization" htmlFor="au-org" error={errors.organizationId?.message}>
              <select id="au-org" className="form-select form-select-sm" {...register('organizationId')}>
                <option value={0}>Select organization…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </FormField>
            <FormField label="Role" htmlFor="au-role">
              {/* Manager (1) retired — only User / Admin are assignable. */}
              <select id="au-role" className="form-select form-select-sm" {...register('role')}>
                <option value={0}>User</option>
                <option value={2}>Admin</option>
              </select>
            </FormField>
          </div>
          {isEdit && <div className="ast-field-hint">Leave the password fields blank to keep the current password.</div>}
        </section>

        {/* Agent Info */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Agent Info</h3>
          <FormField label="Name" htmlFor="au-name">
            <input id="au-name" className="form-control form-control-sm" {...register('name')} />
          </FormField>
          <FormField label="Address" htmlFor="au-address" className="mt-2">
            <input id="au-address" className="form-control form-control-sm" {...register('address')} />
          </FormField>
          <div className="ast-grid-2 mt-2">
            <FormField label="City" htmlFor="au-city">
              <input id="au-city" className="form-control form-control-sm" {...register('city')} />
            </FormField>
            <FormField label="State" htmlFor="au-state">
              <select id="au-state" className="form-select form-select-sm" {...register('state')}>
                {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="Zip" htmlFor="au-zip">
              <input id="au-zip" className="form-control form-control-sm" maxLength={10} {...register('zip')} />
            </FormField>
          </div>
        </section>

        <div className="ast-form-actions">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/admin')}>Back</button>
          <button type="submit" className="btn btn-ast-blue" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isEdit ? 'Update' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}
